import { useAppSelector } from "@/store/hooks";
import { useEffect, useRef, useState } from "react";
import { ProductDataType } from "@/api/ProductsApi";
import { CategoryApi, CategoryDataType } from "@/api/CategoryApi";
import { Button, Col, Form, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";
import styles from "./style.module.scss";
import Select from "react-select";
import { generateSelectOption, goNextOnEnter, goToField, SelectOptionType } from "@utils/Utils";
import { MEASURE_UNITS, PRODUCT_TYPES, TAXES_PERCENTAGES, TAXES_TYPES } from "@utils/Constants";
import { roundCant } from "@utils/Cant";
import { ProductFormSchema } from "./productFormSchema";
import { EDOCUMENT_TEXT_MAX_LENGTH, sanitizeSafeText } from "@amplifyShared/safeText";
import * as yup from 'yup';;
import { showAlert } from "@utils/Alert";
import { OpenCabysSearchPubSub } from "@utils/pubsubs/PubSub";
import SearchCabysOffCanvas from "@components/layout/Offcanvas/SearchCabysOffCanvas";

type GenerateTemporalProductProps = {
  onProductGenerated: (product: ProductDataType, cant: number) => void
};

const GenerateTemporalProduct = (props: GenerateTemporalProductProps) => {
  const { onProductGenerated } = props;
  const currency = useAppSelector((state) => state.currency);
  const [categoryCode, setCategoryCode] = useState<string>("");
  const [cantidad, setCantidad] = useState<string>("");
  const [categories, setCategories] = useState<CategoryDataType[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<SelectOptionType[]>([]);
  const [tempProductData, setTempProductData] = useState<ProductDataType>({} as ProductDataType);

  const [productTypeOptions, setProductTypeOptions] = useState<SelectOptionType[]>([]);
  const [taxTypeOptions, setTaxTypeOptions] = useState<SelectOptionType[]>([]);
  const [taxPercentajeOptions, setTaxPercentajeOptions] = useState<SelectOptionType[]>([]);
  const [measureUnitOptions, setMeasureUnitOptions] = useState<SelectOptionType[]>([]);

  const categoryRef = useRef<HTMLInputElement>(null);
  const cantRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const cabysRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCategoriesOptions()
    setProductTypeOptions(generateSelectOption(PRODUCT_TYPES))
    setTaxTypeOptions(generateSelectOption(TAXES_TYPES))
    setTaxPercentajeOptions(generateSelectOption(TAXES_PERCENTAGES))
    setMeasureUnitOptions(generateSelectOption(MEASURE_UNITS))
    setTimeout(() => {
      categoryRef.current?.select();
      categoryRef.current?.focus();

    });
  }, [])

  const loadCategoriesOptions = async () => {
    const { items } = await CategoryApi.fetchAll();
    const options = items.filter(i => !!i.id).reduce((a, v) => {
      return {
        ...a,
        [`${v.id}`]: v.nombre
      }
    }, {});
    const categoryOptions = generateSelectOption(options as Record<string, string>)
    setCategoryOptions(categoryOptions)

    setCategories(items)
  }


  const setFormData = (prop: keyof ProductDataType, val: any) => {
    let newPropData = {
      ...tempProductData,
      [prop]: val
    }
    if (prop.startsWith("precio")) {
      let price = val;
      const numberPriceVal = Number(price);
      if (!isNaN(numberPriceVal)) {
        price = numberPriceVal
      }
      newPropData = {
        ...newPropData,
        precio1: price,
        precio2: price,
        precio3: price,
        precio4: price,
      }
    }
    setTempProductData(newPropData)
  }

  const loadCategoryByCode = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      const category = categories.find(c => c.codigo === categoryCode);
      populateCategoryData(category)
    }
  }

  const getSelectOption = (options: SelectOptionType[], value?: string | null) => {
    if (value) {
      return options.find(o => o.value === value);
    }
  }

  const onCategoryChange = (newValue: SelectOptionType) => {
    const category = categories.find(c => c.id === newValue.value);
    populateCategoryData(category)
  }

  const populateCategoryData = (category?: CategoryDataType) => {
    if (category?.id) {
      setCategoryCode(`${category.codigo}`);
      setCantidad("1");
      goToField(cantRef)
      setTempProductData({
        categoryId: category.id,
        category: category,
        costo: 0,
        precio1: 0,
        precio2: 0,
        precio3: 0,
        precio4: 0,
        descripcion_fact: category.nombre,
        tipo_producto: category.tipo_producto,
        tipo_impuesto: category.tipo_impuesto,
        codigo_cabys: category.codigo_cabys,
        porcentaje_impuesto: category.porcentaje_impuesto,
        unidad_medida: category.unidad_medida,
        exento: category.exenta,
        temporal: true
      })
    }
  }

  const triggerGenerateTempProduct = (e?: React.KeyboardEvent<HTMLInputElement>) => {
    if (e) {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        generateTempProduct();
      }
    } else {
      generateTempProduct();
    }
  }

  const resetForm = () => {
    setTempProductData({});
    setCantidad("");
    setCategoryCode("");
    goToField(categoryRef)
  }

  const generateTempProduct = async () => {
    try {
      await ProductFormSchema.validate(tempProductData, { abortEarly: false });
      const cantToAdd = roundCant(Number(cantidad));

      if (isNaN(cantToAdd)) {
        showAlert("danger", "La cantidad tiene que ser un numero valido");
        return;
      }
      if (cantToAdd <= 0) {
        showAlert("danger", "La cantidad tiene que ser mayor a 0");
        return;
      }

      // La descripcion del producto temporal termina en el nodo <Detalle> del XML y en el
      // payload que viaja al Step Function, igual que la de un producto del catalogo.
      onProductGenerated({
        ...tempProductData,
        descripcion_fact: sanitizeSafeText(
          tempProductData.descripcion_fact,
          EDOCUMENT_TEXT_MAX_LENGTH,
        ),
      }, cantToAdd);
      resetForm()
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        err.inner.forEach(error => {
          showAlert("danger", error.message);
        });
      }
    }
  }

  const onCabysSelected = (product?: ProductDataType) => {
    const newTempData: ProductDataType = {
      ...tempProductData,
      codigo_cabys: product?.codigo_cabys,
      tipo_impuesto: product?.tipo_impuesto,
      porcentaje_impuesto: product?.porcentaje_impuesto,
      tipo_producto: product?.tipo_producto
    }
    setTempProductData(newTempData)

  }

  return (
    <>
      <Form>
        <Row className="g-2 mb-2 row-cols-sm-auto align-items-center">
          <Col md="2">
            <Label className="pe-3"
              for="temp-cat"
            >
              Categoria
            </Label>
          </Col>
          <Col>
            <InputGroup>
              <Input
                id="temp-cat"
                type="text"
                innerRef={categoryRef}
                value={categoryCode || ""}
                onChange={(e) => setCategoryCode(e.target.value)}
                onKeyDown={loadCategoryByCode}
              />
            </InputGroup>
          </Col>
          <Col md>
            <Select
              name="categoryFilter"
              placeholder="Seleccione Una"
              options={categoryOptions}
              value={getSelectOption(categoryOptions, tempProductData?.categoryId)}
              onChange={(newValue) => onCategoryChange(newValue as SelectOptionType)}
            />
          </Col>
        </Row >
        <Row className="g-2 mb-2 row-cols-sm-auto align-items-center">
          <Col md="2">
            <Label className="pe-3"
              for="temp-cant"
            >
              Cantidad
            </Label>
          </Col>
          <Col>
            <InputGroup>
              <Input
                id="temp-cant"
                type="number"
                step="0.001"
                min="0"
                innerRef={cantRef}
                onKeyDown={(e) => goNextOnEnter(e, priceRef)}
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={{
            offset: 2,
            size: 1
          }}>
            <Label className="pe-3"
              for="temp-price"
            >
              Precio
            </Label>
          </Col>
          <Col md="3" >
            <InputGroup>
              <InputGroupText>
                {currency.currencySymbol}
              </InputGroupText>
              <Input
                id="temp-price"
                type="number"
                step="0.1"
                innerRef={priceRef}
                onKeyDown={(e) => goNextOnEnter(e, descriptionRef)}
                value={tempProductData.precio1 || "0"}
                onChange={(e) => setFormData("precio1", e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>
        <Row className="g-2 mb-2 row-cols-sm-auto align-items-center">
          <Col md="2">
            <Label className="pe-3"
              for="temp-description"
            >
              Descripcion de Factura
            </Label>
          </Col>
          <Col md>
            <InputGroup>
              <Input
                id="temp-description"
                type="text"
                innerRef={descriptionRef}
                onKeyDown={(e) => goNextOnEnter(e, cabysRef)}
                value={tempProductData.descripcion_fact || ""}
                onChange={(e) => setFormData("descripcion_fact", e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row >
        <Row className="g-2 mb-2 row-cols-sm-auto align-items-center">
          <Col md="2">
            <Label className="pe-3"
              for="temp-prod-type"
            >
              Tipo Producto
            </Label>
          </Col>
          <Col md="4">
            <Select
              inputId="temp-prod-type"
              name="categoryFilter"
              placeholder="Seleccione Una"
              value={getSelectOption(productTypeOptions, `${tempProductData?.tipo_producto}`)}
              onChange={(newValue) => setFormData("tipo_producto", newValue?.value)}
              options={productTypeOptions}
            />
          </Col>
          <Col md={{ offset: 1, size: 2 }}>
            <Label className="pe-3"
              for="temp-cabys"
            >
              Codigo CABYS
            </Label>
          </Col>
          <Col md>
            <InputGroup>
              <Input
                id="temp-cabys"
                type="text"
                maxLength={13}
                innerRef={cabysRef}
                onKeyDown={(e) => triggerGenerateTempProduct(e)}
                value={tempProductData.codigo_cabys || ""}
                onChange={(e) => setFormData("codigo_cabys", e.target.value)}
              />
              <InputGroupText className="p-0">
                <Button
                  className="rounded-0"
                  block
                  color="primary"
                  type="button"
                  onClick={() => OpenCabysSearchPubSub.publish({ openOffCanvas: true })}>
                  Buscar
                </Button>
              </InputGroupText>
            </InputGroup>
          </Col>
        </Row >
        <Row className="g-2 mb-2 row-cols-sm-auto align-items-center">
          <Col md="2">
            <Label className="pe-3"
              for="temp-tax-type"
            >
              Tipo de Impuesto
            </Label>
          </Col>
          <Col md="4">
            <Select
              inputId="temp-tax-type"
              name="categoryFilter"
              placeholder="Seleccione Una"
              value={getSelectOption(taxTypeOptions, `${tempProductData?.tipo_impuesto}`)}
              onChange={(newValue) => setFormData("tipo_impuesto", newValue?.value)}
              options={taxTypeOptions}
            />
          </Col>
          <Col md={{ offset: 1, size: 2 }}>
            <Label className="pe-3"
              for="temp-tax-percentage"
            >
              Porcentaje de Impuesto
            </Label>
          </Col>
          <Col md>
            <Select
              inputId="temp-tax-percentage"
              name="categoryFilter"
              placeholder="Seleccione Una"
              value={getSelectOption(taxPercentajeOptions, `${tempProductData?.porcentaje_impuesto}`)}
              onChange={(newValue) => setFormData("porcentaje_impuesto", newValue?.value)}
              options={taxPercentajeOptions}
            />
          </Col>
        </Row >
        <Row className="g-2 mb-2 row-cols-sm-auto align-items-center">
          <Col md="2">
            <Label className="pe-3"
              for="temp-unit"
            >
              Unidad de medida
            </Label>
          </Col>
          <Col md="4">
            <Select
              inputId="temp-unit"
              name="categoryFilter"
              placeholder="Seleccione Una"
              value={getSelectOption(measureUnitOptions, `${tempProductData?.unidad_medida}`)}
              onChange={(newValue) => setFormData("unidad_medida", newValue?.value)}
              options={measureUnitOptions}
            />
          </Col>
          <Col md={{ offset: 1, size: 2 }}>
            <Input
              id="temp-exempt"
              type="checkbox"
              checked={!!tempProductData.exento}
              onChange={(e) => setFormData("exento", e.target.checked)}
            />
            <Label className="ps-3"
              for="temp-exempt"
            >
              Producto Exento
            </Label>
          </Col>
        </Row >

        <Row className="text-end">
          <Col>
            <Button className={styles.addButton} onClick={() => triggerGenerateTempProduct()} color="primary" type="button">
              Agregar
            </Button>
          </Col>
        </Row >
      </Form >
      <SearchCabysOffCanvas
        onCabysSelected={onCabysSelected} />
    </>
  )
}

export default GenerateTemporalProduct;